'use client';

import { lofiArtworkObjectModel } from 'helpers/collection';
import { EXTENDED_STATUSES } from 'helpers/collection/extended-statuses';
import NoImage from 'assets/icons/no-image.svg';
import styles from './object-basics.module.scss';
import { ArtistCultureDisplayer } from 'components/collection/artist-culture-displayer';
import RightsRestrictedMessage from 'components/collection/rights-restricted-message';
import ObjectTitle from 'components/collection/object-title';
import ObjectInfoTable from 'components/collection/object-info-table';
import ChevronR from 'assets/icons/chevron-r.svg';
import LinkButton from 'components/controls/links/link-button';
import ObjectLabel from 'components/collection/object-label';
import HoldingTypeBadges from 'components/collection/holding-type-badges';
import { type IArtworkMinimum } from '@metmuseum/types';
import { usePathname } from 'next/navigation';
import trackClickButton from 'lib/analytics/trackClickButton';
import { routes } from '@metmuseum/routes';
import { useTranslations } from 'next-intl';

const ObjectBasics = ({
	object,
	packageID,
	forceKioskMode = false,
}: ObjectBasicsProps) => {
	const pathname = usePathname();
	const t = useTranslations('collection');
	const isKioskMode = forceKioskMode || pathname?.startsWith?.('/kiosk');
	const formattedObject = lofiArtworkObjectModel(object);

	const { image, imageAlt, primaryImageRestricted, date } = formattedObject;

	//TODO remove all of this analytics stuff.
	// Function to extract section and case number from the URL
	function extractValuesFromURL(url) {
		const parts = url.split('/');
		const section = parts[parts.length - 3];
		const caseNumber = parts[parts.length - 1];
		return {
			section: section,
			caseNumber: caseNumber,
		};
	}

	// Extract values from the pathname
	const { caseNumber } = extractValuesFromURL(pathname);

	let { section } = extractValuesFromURL(pathname);
	switch (section) {
		case 'greek-and-roman':
			section = 'The Greek and Roman Study Collection';
			break;
		case 'luce':
			section = 'Henry R. Luce Center for the Study of American Art';
			break;
		default:
			section = null;
			break;
	}

	const handleLinkClick = () => {
		if (section) {
			trackClickButton({
				buttonClicked: 'View Full Object Record',
				contentType: 'Gallery Viewer',
				contentName: section,
				caseNumber,
				objectTitle: formattedObject?.title,
				objectId: formattedObject?.objectID,
				accessionNumber: formattedObject?.accessionNumber,
			});
		}
	};

	return (
		<div className={styles.content}>
			<figure className={styles.imageWrapper}>
				{image ? (
					/* eslint-disable @next/next/no-img-element */
					<img
						className={`${styles.image} ${primaryImageRestricted ? styles.restricted : ''}`}
						alt={imageAlt}
						loading="lazy"
						src={image}
					/>
				) : (
					/* eslint-enable @next/next/no-img-element */
					<div className={styles.imageWrapper}>
						<NoImage className={styles.noImage} />
					</div>
				)}
				{primaryImageRestricted && <RightsRestrictedMessage />}
			</figure>

			<div className={styles.textWrapper}>
				<div className={styles.text}>
					<div>
						<ObjectTitle title={formattedObject?.title} />
						<div className={styles.attribution}>
							<div>
								<ArtistCultureDisplayer object={formattedObject} />
							</div>
							{date && <div>{date}</div>}
						</div>
					</div>

					{object?.extendedStatuses && object.extendedStatuses.length > 0 && (
						<HoldingTypeBadges
							extendedStatuses={object.extendedStatuses}
							variant="modal"
						/>
					)}

					<ObjectLabel
						object={object}
						packageID={packageID}
						isKioskMode={isKioskMode}
					/>

					<ObjectInfoTable object={formattedObject} />
					{!isKioskMode &&
						!object?.extendedStatuses?.includes(
							EXTENDED_STATUSES.RETURNED_TO_LENDER_NOT_RESTITUTED,
						) && (
							<LinkButton
								prefetch={false}
								rightIcon={<ChevronR />}
								text={t('viewFullObjectRecord')}
								href={routes.object(object?.objectID)}
								onClick={handleLinkClick}
							/>
						)}
				</div>
			</div>
		</div>
	);
};
interface ObjectBasicsProps {
	object: IArtworkMinimum;
	packageID: number;
	forceKioskMode?: boolean;
}

export default ObjectBasics;
